/**
 * The live AI call dock's wire (ring R5): the stream of AI calls going on now,
 * and "end the call".
 *
 * A HAND-WRITTEN MIRROR of the server's contract
 * (`apps/api/src/modules/crm-calls/lib/outbound/live/types.ts`, sent by
 * `outbound-live.routes.ts`). `aiLive.test.ts` reads that file and holds
 * `LIVE_PHASES`, `LIVE_ROLES` and `LIVE_EVENTS` against it, so a word added on
 * the server fails a test here instead of reaching the dock as a raw key.
 *
 * Every event is read through a reader rather than trusted: the stream lives
 * for hours, and one body from a server a deploy ahead or behind must not take
 * the shell down. A payload that cannot be read is dropped, never thrown.
 *
 * React-free, so the readers run under plain node.
 */
import { API_BASE, ApiError, api } from "./api";

// ---------------------------------------------------------------- mirror

/** Where one call stands. Mirrors `LIVE_PHASES`. */
export const LIVE_PHASES = ["dialing", "ringing", "talking", "ended"] as const;
export type LivePhase = (typeof LIVE_PHASES)[number];

/** Who said a line. Mirrors `LIVE_ROLES`. */
export const LIVE_ROLES = ["callee", "ai"] as const;
export type LiveRole = (typeof LIVE_ROLES)[number];

/** The stream's event names. Mirrors `LIVE_EVENTS`. */
export const LIVE_EVENTS = ["snapshot", "call", "turn", "gone"] as const;

/** Where a takeover stands. Mirrors `TAKEOVER_STAGES`. */
export const TAKEOVER_STAGES = ["none", "ringing", "joined", "handed", "bridged", "failed"] as const;
export type TakeoverStage = (typeof TAKEOVER_STAGES)[number];

/** How the operator's own leg reaches the room. Mirrors `TAKEOVER_VIAS`. */
export const TAKEOVER_VIAS = ["browser", "mobile"] as const;
export type TakeoverVia = (typeof TAKEOVER_VIAS)[number];

/** The person's half of a taken-over call (ring R6c). Mirrors `TAKEOVER_RECORDING_STATES`. */
export const TAKEOVER_RECORDING_STATES = ["recording", "stopped", "none"] as const;
export type TakeoverRecordingState = (typeof TAKEOVER_RECORDING_STATES)[number];

/** The attempt on one call, as this operator may see it. */
export interface LiveTakeover {
  stage: TakeoverStage;
  /** "mobile" or "browser"; empty when the server said nothing. */
  via: string;
  /** Why it did not happen, from `TAKEOVER_ERRORS`. */
  failure: string | null;
  /** This operator is the one taking the call over. */
  mine: boolean;
}

export interface LiveTurn {
  /** Arrival number per call: tells lines apart. */
  seq: number;
  role: LiveRole;
  text: string;
  /** ISO time the words were said: orders the lines. */
  at: string;
}

/** One AI call as this operator may see it. Mirrors `LiveCallView`. */
export interface LiveCall {
  id: string;
  test: boolean;
  phase: LivePhase;
  status: string;
  companyName: string | null;
  regCode: string | null;
  domain: string | null;
  toNumber: string;
  intendedNumber: string | null;
  campaignId: string | null;
  campaignName: string | null;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  answeredBy: string | null;
  outcome: string | null;
  disposition: string | null;
  /** Null until the result is written. */
  summary: string | null;
  stopped: boolean;
  recordObjected: boolean;
  /** This operator placed the call. */
  mine: boolean;
  /**
   * When the AI's own part ended. On a call somebody took over this is NOT the
   * end of the call: the two people went on talking.
   */
  aiEndedAt: string | null;
  /** The attempt on this call, if there is or was one. */
  takeover: LiveTakeover | null;
  /** May this operator press "take over" right now? */
  canTakeOver: boolean;
  /**
   * The person's half after a takeover: "recording" while Twilio records it,
   * "stopped" once somebody stopped it, "none" on every other call.
   */
  personRecording: TakeoverRecordingState;
  /** May this operator stop that recording right now? The stop route's own rule. */
  canStopRecording: boolean;
  turns: LiveTurn[];
}

/** A card this operator may no longer have. Mirrors `LiveGoneEvent`. */
export interface LiveGoneEvent {
  callLogId: string;
  reason: string;
  at: string;
}

export interface LiveSnapshot {
  serverNow: string;
  calls: LiveCall[];
}

export interface LiveTurnEvent {
  callLogId: string;
  turn: LiveTurn;
}

// ---------------------------------------------------------------- reading

type Raw = Record<string, unknown>;

const isObject = (v: unknown): v is Raw => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const text = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const time = (v: unknown): string | null => (typeof v === "string" && Number.isFinite(Date.parse(v)) ? v : null);

export function readLiveTurn(raw: unknown): LiveTurn | null {
  if (!isObject(raw)) return null;
  const seq = raw.seq;
  const at = time(raw.at);
  const words = text(raw.text);
  if (typeof seq !== "number" || !Number.isInteger(seq) || seq < 1 || !at || !words) return null;
  if (!(LIVE_ROLES as readonly unknown[]).includes(raw.role)) return null;
  return { seq, role: raw.role as LiveRole, text: words, at };
}

export function readLiveCall(raw: unknown): LiveCall | null {
  if (!isObject(raw)) return null;
  const id = text(raw.id);
  const startedAt = time(raw.startedAt);
  const toNumber = text(raw.toNumber);
  if (!id || !startedAt || !toNumber) return null;
  if (!(LIVE_PHASES as readonly unknown[]).includes(raw.phase)) return null;
  return {
    id,
    test: raw.test === true,
    phase: raw.phase as LivePhase,
    status: text(raw.status) ?? "",
    companyName: text(raw.companyName),
    regCode: text(raw.regCode),
    domain: text(raw.domain),
    toNumber,
    intendedNumber: text(raw.intendedNumber),
    campaignId: text(raw.campaignId),
    campaignName: text(raw.campaignName),
    startedAt,
    answeredAt: time(raw.answeredAt),
    endedAt: time(raw.endedAt),
    answeredBy: text(raw.answeredBy),
    outcome: text(raw.outcome),
    disposition: text(raw.disposition),
    summary: text(raw.summary),
    stopped: raw.stopped === true,
    recordObjected: raw.recordObjected === true,
    mine: raw.mine === true,
    aiEndedAt: time(raw.aiEndedAt),
    takeover: readLiveTakeover(raw.takeover),
    canTakeOver: raw.canTakeOver === true,
    // A word this client does not know reads as "none": no dot and no button,
    // never a stop offered on a guess.
    personRecording: (TAKEOVER_RECORDING_STATES as readonly unknown[]).includes(raw.personRecording)
      ? (raw.personRecording as TakeoverRecordingState)
      : "none",
    canStopRecording: raw.canStopRecording === true,
    turns: Array.isArray(raw.turns) ? raw.turns.map(readLiveTurn).filter((t): t is LiveTurn => t !== null) : [],
  };
}

export function readLiveTakeover(raw: unknown): LiveTakeover | null {
  if (!isObject(raw)) return null;
  if (!(TAKEOVER_STAGES as readonly unknown[]).includes(raw.stage)) return null;
  return {
    stage: raw.stage as TakeoverStage,
    via: text(raw.via) ?? "",
    failure: text(raw.failure),
    mine: raw.mine === true,
  };
}

export function readLiveGone(raw: unknown): LiveGoneEvent | null {
  if (!isObject(raw)) return null;
  const callLogId = text(raw.callLogId);
  if (!callLogId) return null;
  return { callLogId, reason: text(raw.reason) ?? "taken_over", at: time(raw.at) ?? new Date().toISOString() };
}

export function readLiveSnapshot(raw: unknown): LiveSnapshot | null {
  if (!isObject(raw) || !Array.isArray(raw.calls)) return null;
  const serverNow = time(raw.serverNow);
  if (!serverNow) return null;
  return { serverNow, calls: raw.calls.map(readLiveCall).filter((c): c is LiveCall => c !== null) };
}

export function readLiveTurnEvent(raw: unknown): LiveTurnEvent | null {
  if (!isObject(raw)) return null;
  const callLogId = text(raw.callLogId);
  const turn = readLiveTurn(raw.turn);
  return callLogId && turn ? { callLogId, turn } : null;
}

// ---------------------------------------------------------------- the stream

export interface LiveStreamHandlers {
  onSnapshot(snapshot: LiveSnapshot): void;
  onCall(call: LiveCall): void;
  onTurn(event: LiveTurnEvent): void;
  /** The call left this operator's dock: somebody else took it over. */
  onGone(event: LiveGoneEvent): void;
  /** `permanent`: the browser will not retry by itself (a refused or closed stream). */
  onError(permanent: boolean): void;
}

export interface LiveStream {
  close(): void;
}

/** Open the dock's stream. `close` is safe to call twice. */
export function openAiLiveStream(handlers: LiveStreamHandlers): LiveStream {
  const source = new EventSource(`${API_BASE}/admin/crm/calls/ai-live/stream`);
  let closed = false;

  const on = <T>(name: string, read: (raw: unknown) => T | null, deliver: (value: T) => void) => {
    source.addEventListener(name, (event) => {
      if (closed) return;
      let value: T | null = null;
      try {
        value = read(JSON.parse((event as MessageEvent<string>).data));
      } catch {
        // A frame this client cannot parse is dropped; the next snapshot replaces the state anyway.
      }
      if (value !== null) deliver(value);
    });
  };
  on("snapshot", readLiveSnapshot, handlers.onSnapshot);
  on("call", readLiveCall, handlers.onCall);
  on("turn", readLiveTurnEvent, handlers.onTurn);
  on("gone", readLiveGone, handlers.onGone);

  source.onerror = () => {
    if (closed) return;
    handlers.onError(source.readyState === EventSource.CLOSED);
  };

  return {
    close() {
      if (closed) return;
      closed = true;
      source.close();
    },
  };
}

// ---------------------------------------------------------------- end the call

/** What "end the call" can come back with, as the dock names it. */
export const HANGUP_ERRORS = ["not_found", "not_dialled", "hangup_failed", "forbidden", "failed"] as const;
export type HangupError = (typeof HANGUP_ERRORS)[number];

/** POST `/:id/hangup`: `already` when the call had ended by itself. */
export async function hangupAiCall(callLogId: string): Promise<{ already: boolean }> {
  const raw = await api.post<{ already?: unknown } | null>(
    `/admin/crm/calls/ai-outbound/${encodeURIComponent(callLogId)}/hangup`,
  );
  return { already: raw?.already === true };
}

export function hangupError(err: unknown): HangupError {
  if (!(err instanceof ApiError)) return "failed";
  if (err.status === 403) return "forbidden";
  const code = (err.body as { error?: unknown } | null)?.error;
  return (HANGUP_ERRORS as readonly unknown[]).includes(code) ? (code as HangupError) : "failed";
}
